import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Wallet, Search, FileSpreadsheet, Loader2, ToggleLeft, ToggleRight,
    Plus, Minus, RotateCcw, History as HistoryIcon,
} from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const NZ_MONEY = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 2 });

export default function FirmCashRegister() {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all"); // all | enabled | disabled
    const [activeId, setActiveId] = useState(null);
    const [pendingToggle, setPendingToggle] = useState(null); // client_id

    async function refresh() {
        setLoading(true);
        try {
            const { data } = await api.get("/cash-register/clients");
            setClients(data);
        } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
    }
    useEffect(() => { refresh(); }, []);

    async function toggle(clientId, enabled) {
        if (!isAdmin) { toast.error("Admin only"); return; }
        setPendingToggle(clientId);
        try {
            await api.patch(`/cash-register/clients/${clientId}/access`, { enabled });
            toast.success(enabled ? "Cash Register enabled — client will be billed for this add-on" : "Cash Register disabled");
            setClients((cs) => cs.map((c) => (c.id === clientId ? { ...c, enabled } : c)));
        } catch (e) { toast.error(formatApiError(e)); } finally { setPendingToggle(null); }
    }

    const filtered = useMemo(() => {
        const q = search.toLowerCase().trim();
        return clients.filter((c) => {
            if (filter === "enabled" && !c.enabled) return false;
            if (filter === "disabled" && c.enabled) return false;
            if (q && !c.name.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [clients, search, filter]);

    const enabledCount = clients.filter((c) => c.enabled).length;

    return (
        <div data-testid="firm-cash-register-page">
            <PageHeader
                eyebrow="Cash on hand"
                title="Cash Register"
                subtitle="Enable the Cash on Hand Register add-on per client, and audit their live ledger."
            />

            <div className="mt-6 flex flex-wrap items-center gap-2">
                <FilterPill label={`All (${clients.length})`} active={filter === "all"} onClick={() => setFilter("all")} testId="firm-cash-filter-all" />
                <FilterPill label={`Enabled (${enabledCount})`} active={filter === "enabled"} onClick={() => setFilter("enabled")} testId="firm-cash-filter-enabled" />
                <FilterPill label={`Disabled (${clients.length - enabledCount})`} active={filter === "disabled"} onClick={() => setFilter("disabled")} testId="firm-cash-filter-disabled" />
                <div className="ml-auto relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#5E636E]" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search client…"
                        className="pl-8 w-64"
                        data-testid="firm-cash-search"
                    />
                </div>
            </div>

            <div className="mt-4">
                {loading ? (
                    <div className="text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>
                ) : filtered.length === 0 ? (
                    <EmptyState title="No matches" body="Try clearing the filters or search." />
                ) : (
                    <ul className="space-y-2" data-testid="firm-cash-client-list">
                        {filtered.map((c) => (
                            <li
                                key={c.id}
                                className={`co-card co-card-pad flex items-center gap-3 transition-shadow ${c.enabled ? "" : "opacity-70"}`}
                                data-testid={`firm-cash-client-${c.id}`}
                            >
                                <div className={`grid h-10 w-10 place-items-center rounded-full ${c.enabled ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-[#5E636E]"}`}>
                                    <Wallet className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{c.name}</div>
                                    <div className="mt-0.5 text-[11px] text-[#5E636E]">
                                        {c.enabled ? (
                                            <>Balance <strong className="text-[#0F1115] tabular-nums">{NZ_MONEY.format(c.balance)}</strong>{c.last_entry_at ? ` · last entry ${new Date(c.last_entry_at).toLocaleString("en-NZ")}` : " · no entries yet"}</>
                                        ) : (
                                            <>Add-on disabled — client cannot see the register.</>
                                        )}
                                    </div>
                                </div>
                                {c.enabled && (
                                    <Button size="sm" variant="outline" onClick={() => setActiveId(c.id)} data-testid={`firm-cash-view-${c.id}`}>
                                        View ledger
                                    </Button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => toggle(c.id, !c.enabled)}
                                    disabled={!isAdmin || pendingToggle === c.id}
                                    title={isAdmin ? (c.enabled ? "Disable Cash Register" : "Enable Cash Register") : "Admin only"}
                                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                                        c.enabled ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-zinc-200 text-[#5E636E] hover:bg-zinc-300"
                                    } ${!isAdmin ? "cursor-not-allowed" : ""}`}
                                    data-testid={`firm-cash-toggle-${c.id}`}
                                >
                                    {pendingToggle === c.id ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : c.enabled ? (
                                        <ToggleRight className="h-3.5 w-3.5" />
                                    ) : (
                                        <ToggleLeft className="h-3.5 w-3.5" />
                                    )}
                                    {c.enabled ? "Enabled" : "Disabled"}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {activeId && (
                <LedgerDialog
                    clientId={activeId}
                    client={clients.find((c) => c.id === activeId)}
                    onClose={() => setActiveId(null)}
                />
            )}
        </div>
    );
}


function FilterPill({ label, active, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-full border px-3 py-1.5 text-xs ${active ? "bg-[#171717] text-white border-[#171717]" : "border-[#E4E4E7] text-[#5E636E] hover:bg-zinc-50"}`}
            data-testid={testId}
        >
            {label}
        </button>
    );
}


function LedgerDialog({ clientId, client, onClose }) {
    const [entries, setEntries] = useState([]);
    const [closes, setCloses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [e, d] = await Promise.all([
                    api.get("/cash-register/entries", { params: { client_id: clientId, limit: 500 } }),
                    api.get("/cash-register/day-close", { params: { client_id: clientId, limit: 30 } }),
                ]);
                setEntries(e.data);
                setCloses(d.data);
            } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
        })();
    }, [clientId]);

    async function downloadExport() {
        try {
            const res = await api.get("/cash-register/export", { params: { client_id: clientId }, responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const cd = res.headers["content-disposition"] || "";
            const match = cd.match(/filename="([^"]+)"/);
            a.download = match ? match[1] : "cash_ledger.xlsx";
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Export downloaded");
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="firm-cash-ledger-dialog"
        >
            <div
                className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E4E7]">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Cash Ledger</div>
                        <div className="text-lg font-medium">{client?.name}</div>
                        <div className="mt-0.5 text-[11px] text-[#5E636E]">
                            Balance <strong className="text-[#0F1115] tabular-nums">{NZ_MONEY.format(client?.balance || 0)}</strong> · Read-only firm view
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={downloadExport} data-testid="firm-cash-ledger-export">
                            <FileSpreadsheet className="h-3.5 w-3.5" /> Export
                        </Button>
                        <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-8 text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading ledger…</div>
                    ) : entries.length === 0 ? (
                        <div className="p-8 text-center text-sm text-[#5E636E]">No entries yet — client hasn&apos;t started using the register.</div>
                    ) : (
                        <>
                            <table className="w-full text-sm" data-testid="firm-cash-ledger-table">
                                <thead className="text-[11px] uppercase tracking-wide text-[#5E636E] bg-[#FAFAFA] sticky top-0">
                                    <tr>
                                        <th className="text-left px-4 py-2">Date</th>
                                        <th className="text-left px-4 py-2">Ref</th>
                                        <th className="text-left px-4 py-2">Description</th>
                                        <th className="text-right px-4 py-2">Cash Out</th>
                                        <th className="text-right px-4 py-2">Cash In</th>
                                        <th className="text-right px-4 py-2">Balance</th>
                                        <th className="text-left px-4 py-2">Entered by</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map((e) => (
                                        <tr key={e.id} className={`border-t border-[#E4E4E7] ${e.kind === "adjustment" ? "bg-amber-50/60" : ""}`}>
                                            <td className="px-4 py-2 text-[11px] tabular-nums text-[#5E636E]">{new Date(e.occurred_at).toLocaleString("en-NZ")}</td>
                                            <td className="px-4 py-2 font-mono text-[11px]">{e.reference}</td>
                                            <td className="px-4 py-2">
                                                <div className="flex items-center gap-1.5">
                                                    {e.kind === "adjustment" ? <RotateCcw className="h-3 w-3 text-amber-700" /> : e.cash_in > 0 ? <Plus className="h-3 w-3 text-emerald-700" /> : <Minus className="h-3 w-3 text-rose-700" />}
                                                    <span>{e.description}</span>
                                                </div>
                                                {e.adjustment_reason && (
                                                    <div className="mt-0.5 text-[11px] text-amber-800 italic">Reason: {e.adjustment_reason}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-right tabular-nums text-rose-700">{e.cash_out ? NZ_MONEY.format(e.cash_out) : ""}</td>
                                            <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{e.cash_in ? NZ_MONEY.format(e.cash_in) : ""}</td>
                                            <td className="px-4 py-2 text-right tabular-nums font-semibold">{NZ_MONEY.format(e.running_balance)}</td>
                                            <td className="px-4 py-2 text-[11px] text-[#5E636E]">{e.entered_by_name}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {closes.length > 0 && (
                                <div className="p-4 border-t border-[#E4E4E7] bg-[#FAFAFA]">
                                    <div className="text-[11px] uppercase tracking-wide text-[#5E636E] mb-2 flex items-center gap-1.5">
                                        <HistoryIcon className="h-3 w-3" /> Day closes
                                    </div>
                                    <ul className="space-y-1.5 text-xs">
                                        {closes.map((c) => (
                                            <li key={c.id} className="flex items-center gap-3">
                                                <span className="font-mono text-[11px] text-[#5E636E]">{c.business_date}</span>
                                                <span>Expected {NZ_MONEY.format(c.expected_balance)} · Counted {NZ_MONEY.format(c.counted_balance)}</span>
                                                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${Math.abs(c.variance) < 0.005 ? "bg-emerald-100 text-emerald-800" : c.variance < 0 ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                                                    Var {c.variance >= 0 ? "+" : ""}{NZ_MONEY.format(c.variance)}
                                                </span>
                                                {c.variance_note && <span className="italic text-[#5E636E] truncate">{c.variance_note}</span>}
                                                <span className="ml-auto text-[10px] text-[#5E636E]">by {c.closed_by_name}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
